import { ExternalLink } from "lucide-react";

import { CodeBlock } from "@/components/cinch/CodeBlock";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

import { useRagCorpus } from "../api";

/** Setup guide lives in the repo so it versions with the tooling it describes. */
const SETUP_GUIDE =
  "https://bitbucket.org/cinch2/cinch_ops/src/main/docs/claude-code-rag-setup.md";

/**
 * "Using /rag" — the command-line half of this feature.
 *
 * Audience is anyone driving a coding agent, so it is written around the move
 * that actually saves time (find the existing pattern before writing a new one)
 * rather than around the flag reference, which the guide already covers.
 */
export function RagCommandTab() {
  const corpus = useRagCorpus();

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-6">
      <Card>
        <CardHeader>
          <CardTitle>
            <code className="rounded bg-muted px-1.5 py-0.5">/rag</code> in Claude Code
          </CardTitle>
          <CardDescription>
            The same index this page searches, available from your terminal — so an
            agent can search every Cinch repo mid-task without you pasting files into
            it or checking those repos out.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <CodeBlock
            code={'/rag where subscription MRR is calculated for a contract'}
            label="In any Claude Code session"
          />
          <p className="text-sm text-muted-foreground">
            One-time setup is a script, an allowlist entry, and the command file —
            about five minutes, no dependencies to install. You need to be on the
            Cinch LAN or VPN.
          </p>
          <div>
            <Button asChild variant="outline" size="sm">
              <a href={SETUP_GUIDE} target="_blank" rel="noreferrer">
                <ExternalLink className="mr-2 h-4 w-4" />
                Setup guide
              </a>
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>The move that actually saves time</CardTitle>
          <CardDescription>
            Search before you build, not after you're stuck.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Most of what anyone builds here has a close relative already in the
            codebase. The expensive failure is not an agent that can't write the
            code — it's an agent that writes a <em>second</em> way of doing something
            we already do, because it never saw the first. Different error handling,
            a parallel helper, a new pattern nobody else uses.
          </p>
          <p className="mt-3 text-sm text-muted-foreground">
            So make the first step of a build task a search for the precedent:
          </p>
          <div className="mt-3 flex flex-col gap-2">
            <CodeBlock
              code={"/rag how we paginate a Snowflake-backed list endpoint"}
              label="Before adding a new endpoint"
            />
            <CodeBlock
              code={"/rag how a background sync job reports progress to the UI"}
              label="Before wiring a long-running job"
            />
            <CodeBlock
              code={"/rag how we cache an expensive Snowflake aggregate --lang python"}
              label="Before adding caching"
            />
          </div>
          <p className="mt-3 text-sm text-muted-foreground">
            Then tell the agent to follow what came back. The output carries file
            paths and line ranges, so &ldquo;match the pattern in
            <code className="mx-1 rounded bg-muted px-1 text-xs">backendsafe/…/outbound.py</code>&rdquo;
            is a concrete instruction rather than a vague one.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Queries that work, and queries that don't</CardTitle>
          <CardDescription>
            It matches on meaning, so describe behaviour rather than naming things.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <dl className="flex flex-col gap-3 text-sm">
            <div>
              <dt className="font-medium text-success">Works</dt>
              <dd className="text-muted-foreground">
                &ldquo;where we suppress landlines from SMS sends&rdquo; ·
                &ldquo;retry logic around the QuickBooks sync&rdquo; ·
                &ldquo;how a survey response is persisted&rdquo;. Full sentences
                describing what the code <em>does</em>.
              </dd>
            </div>
            <div>
              <dt className="font-medium text-danger">Doesn't</dt>
              <dd className="text-muted-foreground">
                <code className="rounded bg-muted px-1 text-xs">calculateMRR</code> ·
                <code className="mx-1 rounded bg-muted px-1 text-xs">ERR_TIMEOUT_4021</code> ·
                <code className="rounded bg-muted px-1 text-xs">SNOWFLAKE_ROLE</code>.
                Exact symbols, error strings and config keys are a grep job — semantic
                search will hand you five near-misses instead of the one exact hit.
              </dd>
            </div>
            <div>
              <dt className="font-medium">Narrow when a query drifts</dt>
              <dd className="text-muted-foreground">
                <code className="rounded bg-muted px-1 text-xs">--repo backendsafe</code>{" "}
                and <code className="rounded bg-muted px-1 text-xs">--lang python</code>{" "}
                cut a query that keeps landing in dbt models or YAML schema docs.
              </dd>
            </div>
            <div>
              <dt className="font-medium">Read the scores</dt>
              <dd className="text-muted-foreground">
                Above ~0.65 is usually a real match; below ~0.55 is noise. If
                everything comes back weak, rephrase once before concluding the code
                isn't there.
              </dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Why this beats pointing a big-context agent at the tree</CardTitle>
          <CardDescription>
            The corpus is far larger than any context window, and that gap is the
            whole argument.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            {corpus.data ? (
              <>
                The indexed code is roughly{" "}
                <span className="font-medium text-foreground">
                  {(corpus.data.est_tokens / 1_000_000).toFixed(1)}M tokens
                </span>{" "}
                across {corpus.data.chunks.toLocaleString()} chunks —{" "}
                <span className="font-medium text-foreground">
                  about {corpus.data.context_windows_1m}&times;
                </span>{" "}
                what fits in a 1M-token context window, and that is only the subset we
                index.
              </>
            ) : (
              <>
                The indexed code runs to millions of tokens — many times what fits in
                even a 1M-token context window, and that is only the subset we index.
              </>
            )}{" "}
            You cannot load it. An agent grepping its way through instead burns turns
            guessing filenames, and misses anything named differently from how it
            searched.
          </p>
          <p className="mt-3 text-sm text-muted-foreground">
            Retrieval inverts that: one embedding call returns the ten most relevant
            passages out of the whole corpus in a few hundred milliseconds, and only
            those enter the context. Cheaper, faster, and it finds code whose
            vocabulary you didn't guess — which is exactly the code you were least
            likely to find by hand.
          </p>
          <p className="mt-3 text-sm text-muted-foreground">
            It is a complement to grep, not a replacement. Use{" "}
            <code className="rounded bg-muted px-1 text-xs">/rag</code> to find the
            territory, then grep to pin the exact references.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Putting it in an agent's instructions</CardTitle>
          <CardDescription>
            Agents will not reach for it unless told when to.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <CodeBlock
            code={`Before implementing anything new, run /rag to find how we already
solve the closest existing problem, and follow that pattern unless
there's a stated reason to diverge.

Use /rag for "where/how do we do X" questions across repos.
Use grep for exact symbols, error strings and config keys.
Cite the file and line range you're following.`}
            label="Worth adding to CLAUDE.md or a task prompt"
          />
          <p className="mt-3 text-sm text-muted-foreground">
            The index tracks each repo's <span className="font-medium">main</span>{" "}
            branch, so it shows released code — a colleague's in-flight branch will not
            appear, and an agent should not conclude from a miss that the work doesn't
            exist.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
